/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { DataSource } from '@angular/cdk/table';
import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { FilterOptions, ReportAggregateField, ReportDefinition, ReportDocumentField } from '../../state/report.model';
import { ReportService } from '../../services/report.service';
import {
    auditTime,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgZone } from '@angular/core';
import { PagingOptions } from '../../../../types';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { DashGisUtil } from '../../../shared/dash-gis-util';
import { isEqual } from 'lodash-es';
import { Select, Store } from '@ngxs/store';
import { ReportState } from '../../state/report.state';

export class ReportDataSource extends DataSource<any> {
    reportHtml: SafeHtml;

    count$: Observable<number>;
    loading$: Observable<boolean>;
    disposing$: Observable<void>;
    reportKeys$: Observable<any[]>;
    dataFormKeys$: Observable<any[]>;
    reportHtml$: Observable<SafeHtml>;
    hasHtml$: Observable<boolean>;
    reportData$: Observable<any[]>;
    reportRawData$: Observable<any[]>;
    reportAllData$: Observable<any[] | null>;
    reportAllRawData$: Observable<any[]>;
    reportTotals$: Observable<ReportAggregateField[]>;
    reportAverages$: Observable<ReportAggregateField[]>;
    reportPdf$: Observable<string>;

    protected countSubject: BehaviorSubject<number>;
    protected disposingSubject: Subject<void>;
    protected loadingSubject: BehaviorSubject<boolean>;
    protected reportKeysSubject: BehaviorSubject<any[]>;
    protected dataFormKeysSubject: BehaviorSubject<any[]>;
    protected reportHtmlSubject: BehaviorSubject<SafeHtml>;
    protected hasHtmlSubject: BehaviorSubject<boolean>;
    protected reportDataSubject: BehaviorSubject<any[] | null>;
    protected reportRawDataSubject: BehaviorSubject<any[] | null>;
    protected reportAllDataSubject: BehaviorSubject<any[] | null>;
    protected reportAllRawDataSubject: BehaviorSubject<any[] | null>;
    protected reportTotalsSubject: BehaviorSubject<ReportAggregateField[]>;
    protected reportAveragesSubject: BehaviorSubject<ReportAggregateField[]>;
    protected reportPdfSubject: Subject<string>;

    protected inputChangedSubject = new Subject<void>();

    @Select(ReportState.paging) private paging$: Observable<(id: string, reportName: string) => PagingOptions>;
    @Select(ReportState.filters) private filters$: Observable<(id: string, reportName: string) => FilterOptions[]>;

    private pagingOptions$: Observable<PagingOptions>;
    private filterOptions$: Observable<FilterOptions[]>;
    private currentReportSubscription: Subscription;

    constructor(
        protected logger: LoggerService,
        protected ngZone: NgZone,
        protected store: Store,
        protected sanitizer: DomSanitizer,
        protected reportService: ReportService,
        protected isolatedReport: IsolatedReportService,
        protected forPrint: boolean,
        private definition: ReportDefinition,
        keys: any[]
    ) {
        super();

        this.countSubject = new BehaviorSubject<number>(-1);
        this.count$ = this.countSubject.asObservable();

        this.disposingSubject = new Subject<void>();
        this.disposing$ = this.disposingSubject.asObservable();

        this.loadingSubject = new BehaviorSubject<boolean>(false);
        this.loading$ = this.loadingSubject.asObservable();

        this.reportKeysSubject = new BehaviorSubject<any[]>(keys);
        this.reportKeys$ = this.reportKeysSubject.asObservable().pipe(distinctUntilChanged(isEqual));

        this.dataFormKeysSubject = new BehaviorSubject<any[]>([]);
        this.dataFormKeys$ = this.dataFormKeysSubject.asObservable();

        this.reportHtmlSubject = new BehaviorSubject<SafeHtml>(null);
        this.reportHtml$ = this.reportHtmlSubject.asObservable();

        this.hasHtmlSubject = new BehaviorSubject<boolean>(false);
        this.hasHtml$ = this.hasHtmlSubject.asObservable();

        this.reportDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportData$ = this.reportDataSubject.asObservable();

        this.reportRawDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportRawData$ = this.reportRawDataSubject.asObservable();

        this.reportAllDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportAllData$ = this.reportAllDataSubject.asObservable();

        this.reportAllRawDataSubject = new BehaviorSubject<any[] | null>(null);
        this.reportAllRawData$ = this.reportAllRawDataSubject.asObservable();

        this.reportTotalsSubject = new BehaviorSubject<ReportAggregateField[]>([]);
        this.reportTotals$ = this.reportTotalsSubject.asObservable();

        this.reportAveragesSubject = new BehaviorSubject<ReportAggregateField[]>([]);
        this.reportAverages$ = this.reportAveragesSubject.asObservable();

        this.reportPdfSubject = new Subject<string>();
        this.reportPdf$ = this.reportPdfSubject.asObservable();

        this.pagingOptions$ = this.paging$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged((a, b) => isEqual(a, b))
        );

        this.filterOptions$ = this.filters$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged((a, b) => isEqual(a, b)),
            map((filters: FilterOptions[]) =>
                // do not pass filters without values
                filters.filter((f: FilterOptions) => {
                    switch (f.type) {
                        case 'string':
                        case 'string-exact':
                            return !DashGisUtil.isNullOrEmpty(f.value);
                        case 'number':
                            return (
                                !DashGisUtil.isNullOrEmpty(f.min) ||
                                f.min === 0 ||
                                !DashGisUtil.isNullOrEmpty(f.max) ||
                                f.max === 0
                            );
                        case 'date':
                            return !!(f.min || f.max);
                    }
                })
            )
        );

        this.initialize();
    }

    connect(collectionViewer: CollectionViewer): Observable<any> {
        return this.forPrint ? this.reportAllData$ : this.reportData$;
    }

    disconnect(collectionViewer: CollectionViewer): void {
        // this.forPrint
        //     ? this.reportAllDataSubject.complete()
        //     : this.reportDataSubject.complete();
    }

    initialize() {
        if (!this.forPrint && this.definition.subType !== 'chart' && this.definition.subType !== 'label') {
            this.inputChangedSubject
                .asObservable()
                .pipe(takeUntil(this.disposing$))
                .subscribe({
                    next: () => this.loadReportData(),
                });

            combineLatest([this.reportKeys$, this.pagingOptions$, this.filterOptions$])
                .pipe(
                    distinctUntilChanged(isEqual),
                    // only take the last if fired within x time, this prevents the same report
                    // being fired multiple times when 2 or more of the above params are
                    // changed together (programmatically)
                    auditTime(150),
                    takeUntil(this.disposing$)
                )
                .subscribe({
                    next: () => this.inputChangedSubject.next(),
                });
        }
    }

    dispose() {
        this.disposingSubject.next();

        this.hasHtmlSubject.complete();
        this.reportHtmlSubject.complete();
        this.reportAveragesSubject.complete();
        this.reportTotalsSubject.complete();
        this.reportDataSubject.complete();
        this.reportRawDataSubject.complete();
        this.reportAllDataSubject.complete();
        this.countSubject.complete();
        this.reportPdfSubject.complete();
        this.loadingSubject.complete();

        this.disposingSubject.complete();
    }

    loadReportData() {
        // this.resultCountSubject.next(-1);

        const reportResponse$ = combineLatest([this.pagingOptions$, this.filterOptions$]).pipe(
            debounceTime(0),
            distinctUntilChanged(isEqual),
            take(1),
            switchMap((options) => this.loadReportDataInternal(options[0], options[1])),
            shareReplay()
        );

        reportResponse$
            .pipe(
                filter((response) => response && 'reportHtml' in response),
                map((response) => this.sanitizer.bypassSecurityTrustHtml(response.reportHtml))
            )
            .subscribe(
                (html) => {
                    this.reportHtml = html;
                    this.hasHtmlSubject.next(true);
                    this.reportHtmlSubject.next(html);
                },
                (err) => this.logger.error('reportHtml:', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'dataFormKeys' in response),
                map((response) => response.dataFormKeys)
            )
            .subscribe(
                (keys) => this.dataFormKeysSubject.next(keys),
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'data' in response),
                map((response) => response.data),
                tap((data) => this.reportRawDataSubject.next(data)),
                map((data) => this.shapeData(data))
            )
            .subscribe(
                (data) => {
                    this.reportDataSubject.next(data);
                },
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'count' in response),
                map((response) => response.count),
                tap((count) => this.countSubject.next(count))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'totals' in response),
                map((response) => response.totals),
                tap((totals) => this.reportTotalsSubject.next(totals))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'averages' in response),
                map((response) => response.averages),
                tap((averages) => this.reportAveragesSubject.next(averages))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'pdf' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    this.reportPdfSubject.next(response.pdf);
                },
                (err) => this.logger.error('PDF pipe', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'csv' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    const csvBlob = new Blob([response.csv], { type: 'text/csv' });
                    this.reportService.downloadCSV(csvBlob);
                },
                (err) => this.logger.error('csv pipe', err)
            );
    }

    loadAllReportData() {
        const reportResponse$ = combineLatest([this.pagingOptions$, this.filterOptions$]).pipe(
            debounceTime(0),
            distinctUntilChanged(isEqual),
            take(1),
            map((options) => {
                options[0] = {
                    page: null,
                    limit: null,
                    sortColumn: options[0]?.sortColumn,
                    sortDirection: options[0]?.sortDirection,
                };
                return options;
            }),
            switchMap((options) => this.loadReportDataInternal(options[0], options[1])),
            shareReplay()
        );

        reportResponse$
            .pipe(
                filter((response) => 'reportHtml' in response),
                map((response) => this.sanitizer.bypassSecurityTrustHtml(response.reportHtml))
            )
            .subscribe(
                (html) => {
                    this.reportHtml = html;
                    this.hasHtmlSubject.next(true);
                    this.reportHtmlSubject.next(html);
                },
                (err) => this.logger.error('reportHtml:', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'data' in response),
                map((response) => response.data),
                tap((data) => this.reportAllRawDataSubject.next(data)),
                map((data) => this.shapeData(data))
            )
            .subscribe(
                (data) => this.reportAllDataSubject.next(data),
                (err) => this.logger.error(err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'count' in response),
                map((response) => response.count),
                tap((count) => this.countSubject.next(count))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'totals' in response),
                map((response) => response.totals),
                tap((totals) => this.reportTotalsSubject.next(totals))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'averages' in response),
                map((response) => response.averages),
                tap((averages) => this.reportAveragesSubject.next(averages))
            )
            .subscribe();

        reportResponse$
            .pipe(
                filter((response) => response && 'pdf' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    this.reportPdfSubject.next(response.pdf);
                },
                (err) => this.logger.error('PDF pipe', err)
            );

        reportResponse$
            .pipe(
                filter((response) => response && 'csv' in response),
                take(1)
            )
            .subscribe(
                (response) => {
                    const csvBlob = new Blob([response.csv], { type: 'text/csv' });
                    this.reportService.downloadCSV(csvBlob);
                },
                (err) => this.logger.error('csv pipe', err)
            );
    }

    clearAllReportData() {
        this.reportAllDataSubject.next(null);
        this.reportAllRawDataSubject.next(null);
    }

    shapeData(data: any[]) {
        // Detail reports need data structured as one row per field. In order to
        // reuse the cell templates, we create a structure that will match the 'normal'
        // layout, adding the label taken from the field definition 'display' entry:
        //
        // { label: <fieldDef.display>, <fieldname>: <value>, ... extra fields }
        //
        // extra fields might include the 'text' field for video links, for example.

        if (!Array.isArray(data)) {
            return DashGisUtil.clone(data);
        }

        const row = data[0];

        if (data.length !== 1 || this.definition.filters.length > 0) {
            return DashGisUtil.clone(data);
        }

        // using reduce allows us to filter out fields that won't be displayed, and end up added into
        // the related row
        return this.definition.fields.reduce((acc, fieldDef) => {
            if (fieldDef.hidden || (this.forPrint && fieldDef.disablePrint)) {
                return acc;
            }
            const rowObject: any = {};
            if (fieldDef.name in row) {
                rowObject[fieldDef.name] = row[fieldDef.name];
            }
            rowObject.label = fieldDef.display;
            rowObject.field = fieldDef;
            switch (fieldDef.type) {
                case 'string':
                    break;
                case 'number':
                    break;
                case 'date':
                    break;
                case 'document':
                    const docFieldDef = fieldDef as ReportDocumentField;
                    rowObject[docFieldDef.text] = row[docFieldDef.text];
                    rowObject[docFieldDef.documentPath] = row[docFieldDef.documentPath];
                    break;
                case 'link':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    break;
                case 'report':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    rowObject[fieldDef['reportName']] = row[fieldDef['reportName']];
                    if ('reportKey' in fieldDef) {
                        rowObject[fieldDef.reportKey] = row[fieldDef.reportKey];
                    }
                    break;
                case 'video':
                    rowObject[fieldDef.text] = row[fieldDef.text];
                    break;
            }
            acc.push(rowObject);
            return acc;
        }, []);
    }

    getReportSubType$(): Observable<string> {
        let lastDefinition;
        return this.count$.pipe(
            distinctUntilChanged((a: any, b: any) => {
                const result = isEqual(lastDefinition, this.definition) && isEqual(a, b);
                lastDefinition = this.definition;
                return result;
            }),
            map((count) => {
                let subtype;
                switch (true) {
                    case this.definition.subType === 'label':
                        subtype = 'label';
                        break;
                    case this.definition.subType === 'chart':
                        subtype = 'chart';
                        break;
                    case this.definition.subType === 'csv':
                        subtype = 'csv';
                        break;
                    case count > 1 || this.definition.filters.length > 0:
                        subtype = 'tabular';
                        break;
                    case count === 1:
                        subtype = 'detail';
                        break;
                    case count === 0 && this.definition.filters.length === 0:
                        subtype = 'empty';
                        break;
                    case count < 0:
                        subtype = 'pending';
                        break;
                    default:
                        subtype = this.definition.subType;
                        break;
                }
                return subtype;
            }),
            startWith(this.definition.subType)
        );
    }

    private loadReportDataInternal(pagingOptions?: PagingOptions, filterOptions?: FilterOptions[]) {
        if (this.currentReportSubscription) {
            this.currentReportSubscription.unsubscribe();
        }
        this.ngZone.run(() => {
            this.loadingSubject.next(true);
        });

        if (!pagingOptions) {
            pagingOptions = {
                page: null,
                limit: null,
                sortColumn: null,
                sortDirection: null,
            };
        }

        const reportSubject = new BehaviorSubject<any>({});
        const reportResponse$ = reportSubject.asObservable();
        this.currentReportSubscription = this.isolatedReport
            .runReport({
                report: this.definition,
                keys: this.keys,
                // paginator page is 0-based, api is 1-based
                page: pagingOptions.page || pagingOptions.page === 0 ? pagingOptions.page + 1 : null,
                pageLimit: pagingOptions.limit || null,
                sortColumn: pagingOptions.sortColumn || null,
                sortDirection: pagingOptions.sortDirection || null,
                filterOptions: filterOptions || null,
            })
            .pipe(
                tap((result) => reportSubject.next(result)),
                tap(() => {
                    this.loadingSubject.next(false);
                    // this.ngZone.run(() => {
                    //     this.loadingSubject.next(false);
                    // });
                }),
                take(1)
            )
            .subscribe({
                error: (err) => this.logger.error(err),
            });

        return reportResponse$;
    }

    get id(): string {
        return this.isolatedReport.id;
    }

    set id(value: string) {
        this.isolatedReport.id = value;
    }

    get keys(): Array<any> {
        return this.reportKeysSubject.getValue();
    }

    set keys(value: Array<any>) {
        this.reportKeysSubject.next(value);
    }
}
